import { useState, useEffect, type ReactNode } from 'react';
import { useGame } from '../context/GameContext';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../hooks/useTheme';
import { CompassIcon } from '../components/icons';
import { AppScreen } from '../components/AppScreen';

export function HomePage() {
  const { createGame, joinGame, startDemo, connected } = useGame();
  const { isDark, toggle } = useTheme();

  const [playerName, setPlayerName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [mode, setMode] = useState<'idle' | 'join'>('idle');
  const [error, setError] = useState('');

  // Pre-fill code from URL (e.g. /join/ABCDEF or ?code=ABCDEF)
  useEffect(() => {
    const url = new URL(window.location.href);
    const codeParam = url.searchParams.get('code') ?? url.pathname.replace('/join/', '').replace('/', '');
    if (codeParam && /^[A-Z0-9]{6}$/i.test(codeParam)) {
      setJoinCode(codeParam.toUpperCase());
      setMode('join');
    }
  }, []);

  const validate = () => {
    const name = playerName.trim();
    if (!name) { setError('Please enter your name.'); return null; }
    return name;
  };

  const handleNewGame = () => {
    const name = validate();
    if (!name) return;
    createGame(name);
  };

  const handleJoin = () => {
    const name = validate();
    if (!name) return;
    if (!/^[A-Z0-9]{6}$/i.test(joinCode)) { setError('Please enter a valid 6-character game code.'); return; }
    joinGame(joinCode.toUpperCase(), name);
  };

  const handleDemo = () => {
    const name = validate();
    if (!name) return;
    startDemo(name);
  };

  return (
    <AppScreenHomeShell isDark={isDark} onToggleTheme={toggle}>
      {/* Logo/title */}
      <div className="text-center px-6 pt-4">
        <CompassIcon className="w-12 h-12 mx-auto mb-3 text-atlas-brass dark:text-atlas-brass-dark" />
        <h1 className="font-serif text-4xl font-semibold text-atlas-ink dark:text-atlas-ink-dark tracking-tight">
          Plate Seeker
        </h1>
        <p className="mt-2 text-atlas-ink-soft dark:text-atlas-ink-soft-dark text-sm max-w-[280px] mx-auto">
          Spot out-of-state license plates on your road trip!
        </p>
      </div>

      {!connected && (
        <div className="mt-4 text-center text-sm text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
          Connecting to server…
        </div>
      )}

      <div className="flex-1 flex flex-col justify-center px-6 py-6 gap-3.5 w-full">
        <div>
          <label className="block text-sm font-medium text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-1">
            Your Name
          </label>
          <input
            type="text"
            value={playerName}
            onChange={e => { setPlayerName(e.target.value.slice(0, 12)); setError(''); }}
            placeholder="Up to 12 characters"
            maxLength={12}
            className="w-full px-4 py-3 rounded-xl border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark text-atlas-ink dark:text-atlas-ink-dark placeholder-atlas-ink-soft/60 dark:placeholder-atlas-ink-soft-dark/60 focus:outline-none focus:ring-2 focus:ring-atlas-brass dark:focus:ring-atlas-brass-dark text-base"
          />
        </div>

        {mode === 'idle' ? (
          <>
            <button
              onClick={handleNewGame}
              disabled={!connected}
              className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Game
            </button>
            <button
              onClick={() => { setMode('join'); setError(''); }}
              className="w-full h-[52px] rounded-xl border-[1.5px] border-atlas-ink dark:border-atlas-ink-dark text-atlas-ink dark:text-atlas-ink-dark font-semibold text-[17px] transition-colors hover:bg-atlas-surface dark:hover:bg-atlas-surface-dark"
            >
              Join with a Code
            </button>

            <div className="mt-2 p-4 rounded-xl border border-dashed border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
                No one to play with?
              </span>
              <button
                onClick={handleDemo}
                disabled={!connected}
                className="text-left text-[15px] font-semibold text-atlas-brass dark:text-atlas-brass-dark hover:text-atlas-brass/80 dark:hover:text-atlas-brass-dark/80 disabled:opacity-50"
              >
                Try a Demo Game →
              </button>
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-1">
                Game Code
              </label>
              <input
                type="text"
                value={joinCode}
                onChange={e => { setJoinCode(e.target.value.toUpperCase().slice(0, 6)); setError(''); }}
                placeholder="6-letter code"
                maxLength={6}
                autoFocus
                className="w-full px-4 py-3 rounded-xl border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark text-atlas-ink dark:text-atlas-ink-dark placeholder-atlas-ink-soft/60 dark:placeholder-atlas-ink-soft-dark/60 font-mono text-lg tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-atlas-brass dark:focus:ring-atlas-brass-dark"
              />
            </div>
            <button
              onClick={handleJoin}
              disabled={!connected}
              className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Join Game
            </button>
            <button
              onClick={() => { setMode('idle'); setError(''); }}
              className="text-sm font-medium text-atlas-ink-soft dark:text-atlas-ink-soft-dark hover:text-atlas-ink dark:hover:text-atlas-ink-dark text-center"
            >
              ← Back
            </button>
          </>
        )}

        {error && (
          <p className="text-sm text-atlas-brick dark:text-atlas-brick-dark text-center">{error}</p>
        )}
      </div>

      <p className="text-center pb-2 font-mono text-[11px] tracking-wide text-atlas-ink-soft/70 dark:text-atlas-ink-soft-dark/70">
        50 PTS TO WIN · UP TO 5 PLAYERS
      </p>
    </AppScreenHomeShell>
  );
}

function AppScreenHomeShell({
  isDark,
  onToggleTheme,
  children,
}: {
  isDark: boolean;
  onToggleTheme: () => void;
  children: ReactNode;
}) {
  return (
    <AppScreen>
      <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 flex justify-end pt-[calc(env(safe-area-inset-top)+8px)] px-4">
        <ThemeToggle isDark={isDark} onToggle={onToggleTheme} />
      </div>
      <div className="flex-1 flex flex-col">{children}</div>
    </AppScreen>
  );
}
